/**
 * VariantPicker
 * ------------------------------------------------------------------------------
 * Shared, framework-light variant selector for the native SF2.0 option markup
 * rendered by `snippets/variant-picker.liquid`.
 *
 * Reads native `name="options[<OptionName>]"` inputs, resolves the selected
 * combination to a variant against the product JSON (matching `variant.options`
 * directly — no external lib, unambiguous), keeps the hidden `input[name="id"]`
 * and the URL `?variant=` in sync, and broadcasts a `variant:change` CustomEvent
 * on `document` that PDP behaviors (price, image, waitlist, Shop Pay, sticky bar)
 * subscribe to.
 *
 * External surfaces (e.g. the sticky bar) can drive selection by dispatching a
 * `pdp:select-variant` CustomEvent with `{ detail: { id } }`.
 *
 * Two consumers: the PDP (`src/scripts/sections/pdpForm.js`) takes every default,
 * and the collection card (`src/scripts/components/CollectionProductCard.js`),
 * which opts out of the page-level behaviours — URL sync, inbound `?variant=`,
 * the document-level select event, and the document-level dispatch — because a
 * collection page holds ~24 pickers and none of them owns the page.
 *
 * @namespace VariantPicker
 */

import {isFastBundleProduct} from '@/scripts/utils/fastBundle';

const SELECTORS = {
    optionInputs: '[name^="options"]',
    idInput: 'input[name="id"]',
};

const DEFAULTS = {
    // Keep `?variant=` in the address bar. PDP: yes, the page is the product.
    // Collection card: no — the URL belongs to the collection, and 24 cards
    // would fight over it.
    syncUrl: true,
    // Honour an inbound `?variant=` on init. Collection card: no — one inbound
    // id would be applied by every card that happens to own that variant.
    readInboundVariant: true,
    // Listen for `pdp:select-variant` on document (the sticky bar drives the
    // PDP picker this way). Collection card: no — every card would react.
    listenForExternalSelect: true,
    // Where `variant:change` is dispatched. null => document, which is what the
    // PDP subscribers (pdpForm, sticky bar) listen on. Cards pass their own
    // form so the event stays inside the card.
    eventTarget: null,
};

export default class VariantPicker {
    /**
     * @param {HTMLElement} form - the product <form> element
     * @param {object} product - the product object from {{ product | json }}
     * @param {object} [options] - see DEFAULTS; omitting it keeps PDP behaviour
     */
    constructor(form, product, options = {}) {
        this.form = form;
        this.product = product;
        this.options = {...DEFAULTS, ...options};
        this.eventTarget = this.options.eventTarget || document;
        this.optionNames = product.options || [];
        this.idInput = form.querySelector(SELECTORS.idInput);
        this.optionInputs = Array.from(form.querySelectorAll(SELECTORS.optionInputs));

        this.onOptionChange = this.onOptionChange.bind(this);
        this.onExternalSelect = this.onExternalSelect.bind(this);

        this.bind();
    }

    bind() {
        this.optionInputs.forEach((input) => {
            input.addEventListener('change', this.onOptionChange);
        });

        if (this.options.listenForExternalSelect) {
            document.addEventListener('pdp:select-variant', this.onExternalSelect);
        }
    }

    onOptionChange() {
        this.update(this.currentVariant);
    }

    onExternalSelect(event) {
        const id = event && event.detail && event.detail.id;
        if (id) {
            this.selectVariantById(id);
        }
    }

    /**
     * Selected option values, in product option order.
     * @returns {Array<string|null>}
     */
    getSelectedOptions() {
        return this.optionNames.map((optionName) => {
            const name = `options[${optionName}]`;
            const input = this.optionInputs.find((el) => {
                if (el.name !== name) {
                    return false;
                }

                if (el.type === 'radio') {
                    return el.checked;
                }

                return true;
            });

            return input ? input.value : null;
        });
    }

    findVariant(values) {
        return this.product.variants.find((variant) => {
            const options = variant.options || [];

            if (options.length !== values.length) {
                return false;
            }

            return options.every((option, index) => option === values[index]);
        }) || null;
    }

    get currentVariant() {
        return this.findVariant(this.getSelectedOptions());
    }

    /**
     * Set the option inputs to match a variant and refresh the PDP.
     * @param {object} variant
     */
    selectVariant(variant) {
        if (!variant) {
            return;
        }

        this.optionNames.forEach((optionName, index) => {
            const value = variant.options[index];
            const name = `options[${optionName}]`;

            this.optionInputs
                .filter((el) => el.name === name)
                .forEach((el) => {
                    if (el.type === 'radio') {
                        el.checked = el.value === value;
                    } else {
                        el.value = value;
                    }
                });
        });

        this.update(variant);
    }

    selectVariantById(id) {
        const variant = this.product.variants.find((v) => String(v.id) === String(id));
        if (variant) {
            this.selectVariant(variant);
        }
    }

    /**
     * Refresh the hidden id + URL and broadcast the change. All DOM presentation
     * (price/image/button/waitlist) is handled by `variant:change` subscribers.
     * @param {object|null} variant
     */
    update(variant) {
        if (variant) {
            if (this.idInput) {
                this.idInput.value = variant.id;
                this.idInput.setAttribute('data-first_id', variant.id);
            }

            if (this.options.syncUrl) {
                // history.replaceState can throw in Safari private mode.
                try {
                    const url = new URL(window.location);
                    url.searchParams.set('variant', variant.id);
                    window.history.replaceState({}, '', url);
                } catch (error) {
                    /* noop */
                }
            }
        }

        // CustomEvent does not bubble unless asked to, so dispatching on a form
        // genuinely scopes the event to that form's subscribers.
        this.eventTarget.dispatchEvent(new CustomEvent('variant:change', {
            detail: {
                variant,
                product: this.product,
            },
        }));
    }

    /**
     * Initial selection. Honours an inbound `?variant=`, otherwise falls back to
     * the first available variant. Bundle products (FastBundle) are left alone so
     * the app's own widget can drive the form.
     */
    init() {
        if (isFastBundleProduct(this.product)) {
            // Keep the theme UI (price/button) on a real, buyable variant without
            // forcing the option inputs — the FastBundle widget drives selection.
            // `currentVariant` is null for single-variant bundles (no option inputs;
            // "Default Title" never matches [null]), which would disable the button.
            const variant = this.currentVariant
                || this.product.variants.find((v) => v.available)
                || this.product.variants[0];
            this.update(variant);
            return;
        }

        const inbound = this.options.readInboundVariant
            ? new URLSearchParams(window.location.search).get('variant')
            : null;
        let target = null;

        if (inbound) {
            target = this.product.variants.find((v) => String(v.id) === String(inbound)) || null;
        }

        if (!target) {
            const current = this.currentVariant;
            target = (current && current.available)
                ? current
                : this.product.variants.find((v) => v.available) || current || this.product.variants[0];
        }

        if (target) {
            this.selectVariant(target);
        } else {
            this.update(this.currentVariant);
        }
    }

    destroy() {
        this.optionInputs.forEach((input) => {
            input.removeEventListener('change', this.onOptionChange);
        });

        if (this.options.listenForExternalSelect) {
            document.removeEventListener('pdp:select-variant', this.onExternalSelect);
        }
    }
}
